import type { AppUpdateResult } from '@/app/pwa/appUpdate'

export interface RefreshDeps {
  online: boolean
  signedIn: boolean
  /** Send waiting work first, so nothing the phone holds is older than what it downloads. */
  syncNow: () => Promise<void>
  /** The same download as More → Update farmer list. */
  downloadCaches: () => Promise<void>
  installAppUpdate: () => Promise<AppUpdateResult>
  /** How long the chip waits for the send before downloading anyway. */
  sendWaitMs?: number
}

/** The send keeps going in the background after this; the chip does not wait for every photo. */
export const REFRESH_SEND_WAIT_MS = 15_000

export type RefreshResult =
  | { kind: 'offline' }
  | { kind: 'signedOut'; app: AppUpdateResult }
  | { kind: 'updated'; app: AppUpdateResult }
  | { kind: 'failed'; error: unknown }

/**
 * The header's update chip: send, download the latest lists and settings, then install a newer app
 * if there is one. A failed download never reloads, so the message stays on screen.
 */
export async function refreshPhone(deps: RefreshDeps): Promise<RefreshResult> {
  if (!deps.online) return { kind: 'offline' }
  if (deps.signedIn) {
    // A send that fails leaves the work queued; a slow one carries on in the background. Either
    // way the download still runs, so the chip never spins for as long as the photos take.
    let timer: ReturnType<typeof setTimeout> | undefined
    await Promise.race([
      deps.syncNow().catch(() => undefined),
      new Promise<void>((r) => (timer = setTimeout(r, deps.sendWaitMs ?? REFRESH_SEND_WAIT_MS))),
    ])
    clearTimeout(timer)
    try {
      await deps.downloadCaches()
    } catch (error) {
      return { kind: 'failed', error }
    }
  }
  let app: AppUpdateResult
  try {
    app = await deps.installAppUpdate()
  } catch {
    app = 'unavailable'
  }
  return deps.signedIn ? { kind: 'updated', app } : { kind: 'signedOut', app }
}
